import * as React from 'react';
import { FC, useState } from 'react';

import { ISanityBlockContent, ISanityImage } from '@rbi-ctg/menu';
import { MarketingCardGroup } from 'components/features/components/marketing-card-group';
import { MarketingBasicTile } from 'components/marketing-basic-tile';
import {
  HorizontalPosition,
  MarketingTile,
  ModalPayloadType,
  OnShowModalType,
  TileSize,
} from 'components/marketing-tile';
import SanityBlockRenderer, {
  ISerializers,
  SanityBlockContentLinkProps,
} from 'components/sanity-block-renderer';
import {
  IMarketingTileBasicFragment,
  IMarketingTileFragment,
  IMarketingTileGroup,
} from 'generated/sanity-graphql';
import useDialogModal from 'hooks/use-dialog-modal';
import useMediaQuery from 'hooks/use-media-query';

import {
  LeftContainer,
  QuadTileContainer,
  RightContainer,
  Section,
  StyledAnchor,
  StyledBasicTile,
  StyledLink,
} from './styles';
import { IMarketingTileGroupProps } from './types';

const getAlignment = (val: string) => {
  return HorizontalPosition[val.toUpperCase()];
};

export const Tile = ({
  tile,
  size,
  buttonPosition,
  onShowTermsModal,
  hasBorder,
  additionalPadding,
}: {
  tile: IMarketingTileFragment;
  size: TileSize;
  buttonPosition: HorizontalPosition;
  onShowTermsModal?: OnShowModalType;
  hasBorder?: boolean;
  additionalPadding?: string;
}) => {
  const backgroundImage = tile.backgroundImage?.locale?.app;
  const backgroundImageDescription = tile.backgroundImage?.locale?.imageDescription;
  const textBlock = tile.textBlock?.localeRaw;
  const buttonText = tile.buttonAction?.actionText?.locale;
  const buttonUrl = tile.buttonAction?.actionUrl?.locale || tile.buttonAction?.route;
  const termsText = tile.termsText?.localeRaw;
  const termsButtonText = tile.termsButton?.actionText?.locale;
  const termsButtonUrl = tile.termsButton?.actionUrl?.locale;

  return (
    <MarketingTile
      id={tile._id}
      tileSize={size}
      typographyBlock={textBlock}
      backgroundImage={backgroundImage}
      backgroundImageDescription={backgroundImageDescription}
      buttonText={buttonText || ''}
      buttonUrl={buttonUrl || ''}
      termsText={termsText || ''}
      termsButtonText={termsButtonText || undefined}
      termsButtonUrl={termsButtonUrl || undefined}
      onShowTermsModal={onShowTermsModal}
      textPosition={getAlignment(tile.textAlignment || 'right')}
      hasLockedAspectRatio={tile.lockedAspectRatio || undefined}
      textColor={tile.textColor || undefined}
      buttonPosition={buttonPosition}
      hasBorder={hasBorder}
      additionalPadding={additionalPadding}
    />
  );
};

export interface ITermModalComponentProps {
  headingText?: string;
  contentBlock?: ISanityBlockContent[];
  modalAppearanceEventMessage: string;
  onDismiss: () => void;
}

export const marketingTileLink = ({ children = [], mark = {} }: SanityBlockContentLinkProps) => {
  const [linkText] = children;
  const { href } = mark;

  if (!href || !linkText) {
    return null;
  }

  if (href.startsWith('/')) {
    return <StyledLink to={href}>{linkText}</StyledLink>;
  }

  return (
    <StyledAnchor href={href} target="_blank" rel="noopener noreferrer">
      {linkText}
    </StyledAnchor>
  );
};

const marketingTilesLinkSerializers: ISerializers = {
  marks: {
    link: marketingTileLink,
  },
};

/**
 * MarketingTileGroup purpose
 */
const MarketingTileGroup: FC<IMarketingTileGroupProps> = ({ item, className }) => {
  const isMobile = useMediaQuery('mobile');

  const [pendingData, setPendingData] = useState<ModalPayloadType>({ content: [], heading: '' });
  const [TermsModal, openTermsModal] = useDialogModal({
    modalAppearanceEventMessage: 'Terms and Conditions',
  });

  const onShowTermsModal: OnShowModalType = data => {
    openTermsModal(data);
    setPendingData(data);
  };

  // TODO(MarketingTiles) Remove Marketing Tiles after migration
  let itemTiles: IMarketingTileGroup['tiles'] = null;
  if ('tiles' in item) {
    itemTiles = item?.tiles;
  }
  let cardsContent;
  if ('Cards' in item) {
    cardsContent = <MarketingCardGroup onShowTermsModal={onShowTermsModal} doc={item} />;
  }

  if (itemTiles || cardsContent) {
    return (
      <div className={className}>
        <TermsModal
          heading={pendingData.heading}
          body={
            <SanityBlockRenderer
              content={pendingData?.content}
              serializers={marketingTilesLinkSerializers}
            />
          }
        />
        {/* Marketing Card */}
        {cardsContent}
        {itemTiles?.map((tile, idx) => {
          if (!tile) {
            return null;
          }
          switch (tile.__typename) {
            case 'MarketingTile': {
              const key = `MarketingTile_${tile._id}`;
              // Always half size on mobile
              const size = isMobile ? TileSize.HALF : TileSize.FULL;
              const buttonPosition = getAlignment(tile?.buttonAlignment || 'left');
              return (
                <Section key={key}>
                  <Tile
                    buttonPosition={buttonPosition}
                    size={size}
                    onShowTermsModal={onShowTermsModal}
                    tile={tile as IMarketingTileFragment}
                  />
                </Section>
              );
            }
            case 'MarketingTileBasic': {
              const basicTile = tile as IMarketingTileBasicFragment;
              return (
                <StyledBasicTile
                  key={`MarketingTileBasic_${basicTile._id}`}
                  description={basicTile.marketingBasicTileDescription?.localeRaw}
                  image={basicTile.marketingBasicTileImage?.locale?.app as ISanityImage}
                  title={basicTile.marketingBasicTileTitle?.locale || ''}
                />
              );
            }
            case 'MarketingTilePair': {
              const leftKey = `MarketingTile_left_${tile.left?._id || idx}`;
              const rightKey = `MarketingTile_right_${tile.right?._id || idx}`;
              const key = `MarketingTilePair_${leftKey}_${rightKey}`;
              // Always half size on when it's a pair
              const halfSize = TileSize.HALF;
              const leftButtonPosition = getAlignment(tile.left?.buttonAlignment || 'left');
              const rightButtonPosition = getAlignment(tile.right?.buttonAlignment || 'left');
              const left = tile.left && (
                <Tile
                  buttonPosition={leftButtonPosition}
                  onShowTermsModal={onShowTermsModal}
                  size={halfSize}
                  tile={tile.left as IMarketingTileFragment}
                />
              );
              const right = tile.right && (
                <Tile
                  buttonPosition={rightButtonPosition}
                  onShowTermsModal={onShowTermsModal}
                  size={halfSize}
                  tile={tile.right as IMarketingTileFragment}
                />
              );
              // On mobile the tiles should always be stacked
              if (isMobile) {
                return (
                  <>
                    <Section key={leftKey}>{left}</Section>
                    <Section key={rightKey}>{right}</Section>
                  </>
                );
              }
              // On desktop they are side by side
              return (
                <Section key={key}>
                  <LeftContainer>{left}</LeftContainer>
                  <RightContainer>{right}</RightContainer>
                </Section>
              );
            }
            case 'MarketingTileQuad': {
              const basicTiles = Object.keys(tile)?.map(key => {
                return (
                  key !== '__typename' &&
                  tile[key] && (
                    <MarketingBasicTile
                      key={key}
                      description={tile[key].marketingBasicTileDescription?.localeRaw}
                      image={tile[key].marketingBasicTileImage?.locale?.app as ISanityImage}
                      title={tile[key].marketingBasicTileTitle?.locale || ''}
                    />
                  )
                );
              });
              return basicTiles?.length ? (
                <QuadTileContainer>{basicTiles}</QuadTileContainer>
              ) : null;
            }

            default:
              return null;
          }
        })}
      </div>
    );
  }
  return null;
};

export default MarketingTileGroup;
